import { useState } from "react";
import { ShieldCheck, ArrowRight, CheckCircle2 } from "lucide-react";
import { trpc } from "../../hooks/trpc";

/**
 * MFA-Pflicht GF/Admin nach Fristablauf: keine Sitzung, nur Einrichtung.
 * setupMfa und enableMfa laufen ueber den Einrichtungs-Token (httpOnly-Cookie
 * hub_mfa_setup, 15 Minuten). Danach erneut anmelden, jetzt mit TOTP-Code.
 */
export function MfaSetupRequired({ fristBis, onDone }: { fristBis: string | null; onDone: () => void }) {
  const setupMutation = trpc.auth.setupMfa.useMutation();
  const enableMutation = trpc.auth.enableMfa.useMutation();
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const fristText = fristBis ? new Date(fristBis).toLocaleDateString("de-DE") : null;

  if (done) {
    return (
      <div className="space-y-5 text-center">
        <CheckCircle2 size={28} style={{ color: "#7fd88a", margin: "0 auto" }} />
        <p className="text-sm" style={{ color: "rgba(255,255,255,.8)" }}>
          2FA ist aktiviert. Bitte jetzt erneut anmelden, dabei wird der Code aus der App abgefragt.
        </p>
        <button type="button" className="btn btn-primary w-full py-2.5" onClick={onDone}>
          Zur Anmeldung <ArrowRight size={16} />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-3" style={{ background: "rgba(109, 207, 246, 0.15)" }}>
          <ShieldCheck size={22} style={{ color: "#6DCFF6" }} />
        </div>
        <p className="text-sm font-medium" style={{ color: "#fff" }}>2FA ist fuer GF und Admin Pflicht</p>
        <p className="text-xs mt-2" style={{ color: "rgba(255,255,255,.6)" }}>
          {fristText ? `Die Frist zur Einrichtung ist am ${fristText} abgelaufen. ` : "Die Frist zur Einrichtung ist abgelaufen. "}
          Die Anmeldung ist erst nach Einrichtung moeglich.
        </p>
      </div>

      {error && (
        <div className="p-3 rounded-lg text-xs" style={{ background: "rgba(224, 97, 90, 0.14)", color: "#ffb4b0" }}>{error}</div>
      )}

      {!qrCode ? (
        <button
          type="button"
          className="btn btn-primary w-full py-2.5"
          disabled={setupMutation.isPending}
          onClick={async () => {
            setError(null);
            try {
              const r = await setupMutation.mutateAsync();
              setQrCode(r.qrCode);
              setSecret(r.secret);
            } catch (err: any) {
              setError(err.message ?? "Einrichtung fehlgeschlagen. Bitte erneut anmelden.");
            }
          }}
        >
          2FA jetzt einrichten <ArrowRight size={16} />
        </button>
      ) : (
        <form
          className="space-y-4"
          onSubmit={async e => {
            e.preventDefault();
            setError(null);
            try {
              await enableMutation.mutateAsync({ code });
              setDone(true);
            } catch (err: any) {
              setError(err.message ?? "Code ungueltig");
            }
          }}
        >
          <p className="text-xs" style={{ color: "rgba(255,255,255,.7)" }}>1. QR-Code mit der Authenticator-App scannen:</p>
          <div className="flex justify-center p-3" style={{ background: "#fff", borderRadius: 8 }}>
            <img src={qrCode} alt="TOTP QR-Code" className="w-44 h-44" />
          </div>
          {secret && (
            <code className="block p-2 text-xs select-all break-all" style={{ background: "rgba(255,255,255,.06)", color: "#6DCFF6", borderRadius: 6 }}>
              {secret}
            </code>
          )}
          <p className="text-xs" style={{ color: "rgba(255,255,255,.7)" }}>2. 6-stelligen Code eingeben:</p>
          <input
            type="text"
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, ""))}
            className="input text-center text-xl tracking-[0.5em] font-mono"
            maxLength={6}
            placeholder="000000"
            autoFocus
          />
          <button type="submit" className="btn btn-primary w-full py-2.5" disabled={enableMutation.isPending || code.length !== 6}>
            Aktivieren <ArrowRight size={16} />
          </button>
        </form>
      )}
    </div>
  );
}
